import { createContext, CSSProperties, useContext } from "react";

import { CycloneCommunity } from "./community-context";

export type SlotViewport =
  | "all"
  | "mobile"
  | "tablet"
  | "mobileAndTablet"
  | "desktop"
  | "tabletAndDesktop"
  | "flexTabletAndDesktop";

export interface SlotConfig {
  containerClassName?: string;
  slotClassName?: string;
  style?: CSSProperties;
  viewport: SlotViewport;
}

export interface Slot extends SlotConfig {
  slotName: string;
  hideIfSubscription?: string[];
  hideSlot?: string[];
}

export type StaticAdConfig = Record<string, ReadonlyArray<Slot>>;
export type AdTypes = keyof StaticAdConfig;
export interface CommunitySportsAdConfig {
  sport?: string;
  league?: string;
  team?: string;
}
export type GetAdNetworkPath = (league: string) => string;

export type AdConfig = {
  communityAdConfig?: {
    getCommunityAdConfigPath?: (communityId: number) => string;
    getCommunitySportsAdConfig?: (
      communityId: number,
    ) => CommunitySportsAdConfig;
  };
  networkAdConfig?: {
    getAdNetworkPath?: GetAdNetworkPath;
  };
  staticAdConfig: StaticAdConfig;
};

export interface AppConfig {
  deployEnv: string;
  defaultBrandedContentDisclaimer: string;
}

export interface NetworkConfig {
  affiliateSubtagsSiteKey: string;
  affiliateNCAParticipant: boolean; // i.e., participates in Amazon's NCA program
  appName: string;
  networkId: number;
  networkName: string;
  organizationId: number;
  concertAds: {
    network: string;
    gam: string;
  };
}

export interface CommunityConfig {
  communityApiOrigin: string;
  communityApiUrl: string;
  communityCanonicalDomain: string;
  communityDomain: string;
  communityId: number;
  communityName: string;
  communityOrigin: string;
  communitySailthruSlug?: string;
  communityShortName: string; // e.g. "Verge" instead of "The Verge"
  communityWpSiteId: number;
  primaryCommunityId?: number; // only required for feed-only communities; use to direct global queries to the primary community
  isPrimaryCommunity: boolean;
  primaryCommunityApiOrigin: string;
  /**
   * siteOrigin is determined dynamically at runtime using siteDomain from WP site
   * settings which is retrieved via the global data query and stored in global data
   * context. It will match the origin in the permalinks for the community site in
   * WordPress. This domain can be configured in WordPress and changed during the
   * lifetime of a given deployment so it cannot be determined at build time and
   * expected to stay the same. communityOrigin on the other hand is static and
   * determined based on environment variables. It will always be the same for each
   * environment for a given community site and will always reflect the main external
   * facing origin of the site for a given environment (origin reachable via Fastly).
   */
  siteOrigin: string | null;
  vmPrefix: string;
  voxCreativeCommunityId: number;
  ssoAuthDomain?: string; // for sites with single sign on (SSO) enabled, this is the domain used for authentication
  cycloneCommunities?: Record<string, CycloneCommunity>;
  isFeedOnlyCommunity?: boolean;
}

type CrossPostCommunityConfig = {
  name: string;
  url: string;
  host: string;
  path?: string;
};

export type CrossPostConfig = CrossPostCommunityConfig[];

export interface CoralConfig {
  commentRecThreshold: number;
  commentCalloutThreshold: number;
  enableLegacyID: boolean;
  namePrefix: string;
  cacheFingerprint: string;
  origin: string;
  moderationEmail: string;
  siteId: string;
}

export interface CycloneContentResourceCommentsProps {
  id: string;
  chorusId: number | null;
  chorusUuid: string | null;
  globalId: string;
  permalink: string;
  site: {
    wpId: number;
    domain: string;
    chorusCommunityId: number | null;
    chorusNetworkId: number | null;
    isCrossCommunity: boolean;
  } | null;
  commentsEnabled: boolean;
  commentsMode: string;
}

export interface CycloneCommentsSectionProps extends CycloneContentResourceCommentsProps {
  sectionId: string;
  containerClassName?: string;
}

export interface CycloneConfig {
  serverURL: string;
  commentRecThreshold: number;
  enabled: boolean;
  /** From duet.config.js cycloneCommentsEnabled (single-community brands). */
  commentsEnabled: boolean;
  /** Network-supplied until cyclone comment UI moves into duet-framework. */
  CycloneArticleBody?: React.ComponentType<CycloneContentResourceCommentsProps>;
  /** Network-supplied until cyclone comment UI moves into duet-framework. */
  CycloneCommentsSection?: React.ComponentType<CycloneCommentsSectionProps>;
}

export interface NotificationsConfig {
  apiUrl?: string;
  apiKey?: string;
}

export interface ParselyConfig {
  tagsToInverse: string[];
  enableTraining: boolean;
  enableArticleRecirc: boolean;
  headlineTestingApiKey?: string;
}

export interface PaywallConfig {
  excludedArticleTypes?: string[];
  isWalled: boolean;
  meterExceptions?: string[];
  subXTrackingEnabled: boolean;
  showWallForEditorialSponsorship?: boolean;
}

export interface Newsletter {
  title: string;
  slug: string;
  promoText: {
    html: string;
  };
  listId: string;
  image?: {
    thumbnails: {
      original: {
        url: string;
      };
    };
  } | null;
  frequency?: string;
}

export interface NewslettersPageSection {
  title?: string;
  subtitle?: string;
  subscriberText?: string;
  ctaUrl?: string;
  variant: "thumbnail" | "standard" | "compact" | "paywalledThumbnail";
  /** An array of newsletter slugs to render newsletters in a specific order, or null to render all newsletters from WP */
  newsletters: string[] | null;
  hideHeaderBorder?: boolean;
}

export interface NewslettersConfig {
  newslettersPageTitle: string;
  newslettersPageDek: string;
  newslettersPageMetaDescription: string;
  newslettersPage: NewslettersPageSection[];
  newsletterSectionTitleSize: "xsmall" | "small" | "medium" | "large";
  newsletterLinks?: boolean;
  hideHeaderBorder?: boolean;
  autofillEmail?: boolean;
}

export interface SeoConfig {
  pagesCanonicalMap: Record<string, string>;
  defaultOgImage: string;
}

export interface SubscriptionProviderConfig {
  subscriptionProviderUrl: string;
}

export interface FeatureConfig {
  useLiveStreamUpdates: boolean;
  useQuickPosts: boolean;
  subcategoryLimit: number;
  breadcrumbStrategy: "hierarchy" | "flat";
  useBoostedPosts: boolean;
  // Kill switch for Eater's save-venues-to-lists feature. When off, no save UI renders.
  useSaveVenuesToLists?: boolean;
}

interface RSSFeed {
  url: string;
  label: string;
}
interface RSSFeedSection {
  sectionLabel: string;
  links: RSSFeed[];
}
export type EntitlementBadges = { [key: string]: string };

/*
 * This interface describes configuration data for Corporate subscribers.
 * A corporation (e.g., OpenAI with The Verge) may purchase subscription or
 * membership seats for their employees. Employees with valid email addresses
 * for the corporation should be automatically recognized and granted the
 * subscription or membership entitlement.
 *
 * @interface CorporateEntitlement
 */
export interface CorporateEntitlement {
  /*
   * Name of the corporation or entity. May not be used in code; just added to
   * keep the config readable and maintainable.
   * @property {string} name
   */
  name: string;
  /*
   * Valid email domains for employees, e.g. "openai.com". These domain strings
   * should be as they appear in employee email addresses without the `@`
   * symbol.
   * @property {string[]} emailDomains
   */
  emailDomains: string[]; //
  /*
   * A stable reference ID to be applied to each corporate employees
   * entitlement. This should be stable and known in case we need to
   * deactivate the entitlement later in Firebase.
   * @property {string} refId
   */
  refId: string;
  /*
   * One or more SailThru lists to add new corporate subscribers to.
   * @property {string[]} sailthruLists
   */
  sailthruLists?: string[];
  /*
   * Message to be displayed on the user accoung page.
   * @property {string[]} accountPageMsg
   */
  accountPageMsg?: string;
  /*
   * Price ID to add to the user's Supporting Cast metadata
   * @property {string} supportingCastPriceId
   */
  supportingCastPriceId?: string;
}

export interface MembershipConfig {
  productName?: string;
  // Optional benefits blurb shown beneath the membership heading.
  productDescription?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  // Hide the "Sign Up" link in the nav auth block, leaving only "Login".
  hideSignUp?: boolean;
  /**
   * When true, web account creation requires silent reCAPTCHA v3.
   * Enforced client-side in LoginForm and server-side in firebase-login.
   */
  requireSignupCaptcha?: boolean;
  productUrl?: string;
  navNewsletters?: string;
  navPodcasts?: string;
  navProductName?: string;
  navFeeds?: string;
  newslettersEmbedUrl?: string;
  sentenceProduct?: string;
  entitlementBadges?: EntitlementBadges;
  badgeSectionLabel?: string;
  badgeToggleLabel?: string;
  showAvatarSection?: boolean;
  contactEmail?: string;
  entitlementName?: string;
  showCommentingName?: boolean;
  feedsDek?: string;
  feeds?: RSSFeedSection[];
  faqUrl?: string;
  additionalMenuItems?: {
    path: string;
    linkText: string;
    index: number;
  }[];
  giftArticleMonthlyLimit?: number;
  giftArticleLinkDaysValid?: number;
  corporateEntitlements?: CorporateEntitlement[];
  /*
   * This list is the same as the one in the Stripe product metadata for
   * the brand's subscription (`newsletter_lists_active`).
   * When a user subscribes by paying in Stripe, Jukebox handles signing
   * them up for the SailThru lists in that metadata variable.
   * Since non-stripe subscribers are not going through that flow, we handle
   * the signups to these listsin Duet.
   */
  sailthruLists?: string[];
  /*
   * This property is the name of an ST list a brand wants all registered users
   * added to.
   */
  registeredUsersSailthruList?: string;
  /**
   * Sailthru list to add users to when they sign up via the registration wall
   * (embedded login form). Unlike registeredUsersSailthruList which applies to
   * all new users, this only applies to regwall signups.
   */
  sailthruRegwallList?: string;
}

export interface UserProfileConfig {
  requireCommentingName: boolean;
  commentingNameLabel: string;
  hasPublicProfilePages?: boolean;
  allowEmailChange?: boolean;
}

export interface StnVideoConfig {
  enabled: boolean;
  playerId: string;
  scriptUrl: string;
}

export type StnVideoConfigType = Record<number, StnVideoConfig>;

export type TokenOverrideValue =
  | "comp.coral.comment.rec.color"
  | "comp.coral.comment.unread.color"
  | "sys.color.primary"
  | "sys.color.onPrimary"
  | "sys.color.primaryText"
  | "sys.color.secondary"
  | "sys.color.onSecondary"
  | "sys.color.surface"
  | "comp.notification.standard.alert.color"
  | "comp.notification.standard.alertType.color";

export interface CommunityTokenOverrides {
  [key: string]: Record<TokenOverrideValue, string>;
}

export interface WunderkindConfig {
  siteId?: string;
}

export interface SubscriptionPlanSelectionAnalytics {
  /** Brand identifier for the analytics event (e.g. "verge", "eater_newsletter_kangtown"). */
  name: string;
  /** Page/placement identifier for the analytics event (e.g. "subscribe_page"). */
  position: string;
}

export interface SubscriptionsConfig {
  /** Paths that should not be used as a post-checkout redirect destination. */
  forbiddenRedirectPaths: readonly string[];
  /** Entitlement name stored in firestore */
  entitlement: string;
  /**
   * When present, a `subscription_plan_selection` analytics event is emitted
   * each time a checkout button is clicked in the pricing plan selectors.
   */
  subscriptionPlanSelectionAnalytics?: SubscriptionPlanSelectionAnalytics;
}

export type RetailerLinkOrderConfig = {
  name: string;
  domains: string[];
}[];

export type CommentLocation = "drawer" | "body";

export type ResolvedCommentsProvider = "coral" | "cyclone" | "noop";

export interface CommentsConfig {
  provider: ResolvedCommentsProvider;
  location: CommentLocation;
}

/** Brand-level comments defaults passed from each network app (like FeatureConfig). */
export interface CommentsBrandConfig {
  defaultLocation: CommentLocation;
}

interface ConfigContextType {
  adConfig: AdConfig;
  appConfig: AppConfig;
  commentsConfig: CommentsConfig;
  communityConfig: CommunityConfig;
  coralConfig?: CoralConfig;
  crossPostConfig: CrossPostConfig;
  customArticleFooter?: React.ReactNode;
  cycloneConfig: CycloneConfig;
  networkConfig: NetworkConfig;
  newslettersConfig: NewslettersConfig;
  parselyConfig: ParselyConfig;
  paywallConfig: PaywallConfig;
  seoConfig: SeoConfig;
  stnVideoConfig?: StnVideoConfig;
  subscriptionProviderConfig?: SubscriptionProviderConfig;
  subscriptionsConfig?: SubscriptionsConfig;
  featureConfig: FeatureConfig;
  membershipConfig: MembershipConfig;
  userProfileConfig: UserProfileConfig;
  wunderkindConfig: WunderkindConfig;
  retailerLinkOrderConfig?: RetailerLinkOrderConfig;
}

const ConfigContext = createContext<ConfigContextType | null>(null);

export const useConfigContext = (): ConfigContextType => {
  const configContext = useContext(ConfigContext);

  if (!configContext) {
    throw new Error("Missing Config Context");
  }

  return configContext;
};

/** Resolved comment provider + embed location for the active community. */
export const useCommentsConfig = (): CommentsConfig => {
  return useConfigContext().commentsConfig;
};

export default ConfigContext;
